import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { apiClient } from "../config/api-client";
import { API_ENDPOINTS } from "../config/api-endpoints";
import { QueryKeys } from "../config/query-keys";
import {
  BackendConversationMessagesResponse,
  BackendInfiniteScrollResponse,
} from "../models/api-types";

// Infinite scroll için gelişmiş hook
export const useConversationMessagesInfinite = (
  conversationId: string | null,
  limit: number = 20,
  shareToken?: string | null
) => {
  return useInfiniteQuery({
    queryKey: [
      ...QueryKeys.CONVERSATIONS.messages(conversationId || ""),
      "infinite",
      shareToken || null,
    ],
    initialPageParam: 1,
    queryFn: async ({ pageParam }): Promise<BackendInfiniteScrollResponse> => {
      if (!conversationId) {
        throw new Error("Conversation ID is required");
      }

      const queryParams = new URLSearchParams();
      queryParams.set("page", String(pageParam));
      queryParams.set("limit", String(limit));
      if (shareToken) {
        queryParams.set("token", shareToken);
      }

      const url = `${API_ENDPOINTS.getMessagesInfiniteScroll(
        conversationId
      )}?${queryParams.toString()}`;
      const response = await apiClient.get<BackendInfiniteScrollResponse>(url);
      
      // ✅ CRITICAL: Check for error in response (API client resolves 4xx errors)
      // Backend error format: { success: false, error: { code, message } } or { code, message }
      if (response && typeof response === 'object') {
        const errorCode = (response as any)?.error?.code || (response as any)?.code;
        const errorMessage = (response as any)?.error?.message || (response as any)?.message;
        const success = (response as any)?.success;
        
        // If it's an error response, throw it so React Query can handle it
        if (success === false || errorCode || (errorMessage && !(response as any)?.data)) {
          const error = new Error(errorMessage || "Conversation error") as any;
          error.code = errorCode;
          error.response = {
            data: response,
            status: 400,
          };
          throw error;
        }
      }
      
      return response;
    },
    enabled: !!conversationId,
    staleTime: 5 * 60 * 1000, // 5 dakika
    gcTime: 10 * 60 * 1000, // 10 dakika
    getNextPageParam: (lastPage: BackendInfiniteScrollResponse, allPages) => {
      // hasMore field'ını kullan
      if (!lastPage.data.hasMore) {
        return undefined;
      }
      return allPages.length + 1;
    },
    getPreviousPageParam: (
      _firstPage: BackendInfiniteScrollResponse,
      allPages
    ) => {
      // İlk sayfadan önce sayfa yok
      if (allPages.length <= 1) {
        return undefined;
      }
      return allPages.length - 1;
    },
  });
};

// Mevcut hook (geriye uyumluluk için)
export const useConversationMessages = (conversationId: string | null) => {
  return useQuery({
    queryKey: QueryKeys.CONVERSATIONS.messages(conversationId || ""),
    queryFn: async (): Promise<BackendConversationMessagesResponse> => {
      if (!conversationId) {
        throw new Error("Conversation ID is required");
      }

      const response = await apiClient.get<BackendConversationMessagesResponse>(
        API_ENDPOINTS.getMessagesFromConversation(conversationId)
      );
      return response;
    },
    enabled: !!conversationId,
    staleTime: 5 * 60 * 1000, // 5 dakika
    gcTime: 10 * 60 * 1000, // 10 dakika
  });
};
